<template>
  <div>
    <h2 class="text-xl font-semibold text-gray-900 mb-4">Slack alerts ({{ alerts.length }})</h2>
    <div class="bg-white rounded-lg shadow h-[300px] p-4">
      <!-- Loading State -->
      <div v-if="isLoading" class="h-full flex items-center justify-center">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Loading alerts...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="h-full flex items-center justify-center">
        <div class="text-center">
          <div class="text-red-500 mb-4">
            <svg class="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Failed to load alerts</h3>
            <p class="text-gray-600 mb-4">{{ error }}</p>
            <button
              @click="fetchAlerts"
              class="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State (Original Design) -->
      <div v-else-if="alerts.length === 0" class="h-full flex items-center justify-center">
        <div class="text-center">
          <div class="text-gray-400 mb-4">
            <svg
              class="w-12 h-12 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 17h5l-5 5v-5zM12 7a4 4 0 100 8 4 4 0 000-8z"
              />
            </svg>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No alerts</h3>
            <p class="text-gray-600">Shows you APV and Traffic source slack alerts</p>
          </div>
        </div>
      </div>

      <!-- Alerts List (Scrollable) -->
      <div v-else class="w-full h-full overflow-y-auto">
        <div class="space-y-3">
          <div
            v-for="alert in alerts"
            :key="`${alert.projectId}-${alert.clipId}-${alert.finalEditId}`"
            class="rounded-lg transition-colors"
          >
            <div class="flex items-start space-x-3">
              <!-- Video Thumbnail -->
              <div class="flex-shrink-0">
                 <div class="bg-gray-800 rounded-lg relative overflow-hidden" style="width: 107px; height: 60px;">
                  <!-- Placeholder thumbnail - you can replace with actual video thumbnail -->
                  <div class="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                    <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                    </svg>
                  </div>
                  <!-- Duration overlay -->
                  <div class="absolute bottom-1 right-1 bg-black bg-opacity-75 text-white text-xs px-1 py-0.5 rounded">
                    0:25
                  </div>
                </div>
              </div>

              <!-- Alert Content -->
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-medium text-gray-900 mb-3 truncate" :title="alert.clipTitle">
                  {{ alert.clipTitle.length > 32 ? alert.clipTitle.substring(0, 32) + '...' : alert.clipTitle }}
                </h3>
                <div class="flex items-center justify-between">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ alert.alertCount }} notification{{ alert.alertCount !== 1 ? 's' : '' }}
                  </span>
                  <button
                    @click="handleAlertClick(alert)"
                    class="text-purple-600 hover:text-purple-700 text-sm font-medium transition-colors"
                  >
                    View alerts
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { alertsApi } from '@/api/modules/alerts'
import type { DashboardAlert } from '@/interface/dashboard'

const router = useRouter()

// State
const alerts = ref<DashboardAlert[]>([])
const isLoading = ref(false)
const error = ref('')

// Fetch alerts from API
const fetchAlerts = async () => {
  try {
    isLoading.value = true
    error.value = ''

    const response = await alertsApi.getDashboardAlerts()
    alerts.value = response.alerts
  } catch (err: any) {
    console.error('Failed to fetch dashboard alerts:', err)

    if (err.response?.status === 403) {
      error.value = 'You don\'t have permission to view alerts'
    } else if (err.response?.status === 404) {
      error.value = 'Alerts service not available'
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else {
      error.value = 'Failed to load alerts. Please try again.'
    }
  } finally {
    isLoading.value = false
  }
}

// Handle alert click - navigate to the voting page with alerts fragment
const handleAlertClick = (alert: DashboardAlert) => {
  // Navigate to the voting page with #alerts fragment
  router.push(`/projects/${alert.projectId}/clips/${alert.clipId}/voting#alerts`)
}

// Load alerts on component mount
onMounted(() => {
  fetchAlerts()
})
</script>
